// src/components/infografia/preview/PreviewModal.jsx
import React, { useRef, useEffect, useState } from 'react';
import { FaTimes, FaDownload } from 'react-icons/fa';
import jsPDF from 'jspdf';
import Swal from 'sweetalert2';

const PreviewModal = ({ canvasData, marca, onClose, fabricCanvas, prepareForExport, isPreviewOnly = false }) => {
    const previewRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [isExportingPNG, setIsExportingPNG] = useState(false);
    const [isExportingPDF, setIsExportingPDF] = useState(false);
    const [isGeneratingPreview, setIsGeneratingPreview] = useState(true);

    // Generar preview del canvas cuando se abre el modal
    useEffect(() => {
        // Si es preview only (sin canvas de Fabric.js), crear un canvas temporal con Fabric.js
        if (isPreviewOnly && !fabricCanvas) {
            const generatePreview = async () => {
                setIsGeneratingPreview(true);
                try {
                    // Importar Fabric.js dinámicamente
                    const { Canvas, Text, Rect, Circle, Image: FabricImage, Gradient } = await import('fabric');
                    const QRCode = (await import('qrcode')).default;

                    // Crear un canvas temporal off-screen
                    const tempCanvasElement = document.createElement('canvas');
                    tempCanvasElement.width = canvasData.dimensiones.ancho;
                    tempCanvasElement.height = canvasData.dimensiones.alto;

                    // Crear canvas de Fabric.js
                    const tempFabricCanvas = new Canvas(tempCanvasElement, {
                        width: canvasData.dimensiones.ancho,
                        height: canvasData.dimensiones.alto,
                        backgroundColor: '#FFFFFF'
                    });

                    // Renderizar fondo
                    if (canvasData.fondo.tipo === 'color') {
                        const color = typeof canvasData.fondo.valor === 'string'
                            ? canvasData.fondo.valor
                            : canvasData.fondo.valor?.color || '#FFFFFF';
                        tempFabricCanvas.backgroundColor = color;
                    } else if (canvasData.fondo.tipo === 'gradiente' && canvasData.fondo.valor) {
                        const grad = canvasData.fondo.valor;
                        tempFabricCanvas.setBackgroundColor(
                            new Gradient({
                                type: 'linear',
                                coords: {
                                    x1: 0,
                                    y1: 0,
                                    x2: grad.direccion === 'horizontal' ? canvasData.dimensiones.ancho : 0,
                                    y2: grad.direccion === 'vertical' ? canvasData.dimensiones.alto : 0
                                },
                                colorStops: [
                                    { offset: 0, color: grad.colorInicio || '#FFFFFF' },
                                    { offset: 1, color: grad.colorFin || '#000000' }
                                ]
                            }),
                            () => tempFabricCanvas.renderAll()
                        );
                    }

                    // Cargar elementos
                    for (const elemento of canvasData.elementos || []) {
                        try {
                            switch (elemento.tipo) {
                                case 'texto':
                                    const text = new Text(elemento.propiedades.contenido || '', {
                                        left: elemento.posicion.x,
                                        top: elemento.posicion.y,
                                        fontSize: elemento.propiedades.tamaño || 24,
                                        fill: elemento.propiedades.color || '#000000',
                                        fontFamily: elemento.propiedades.fuente || 'Arial',
                                        fontWeight: elemento.propiedades.negrita ? 'bold' : 'normal',
                                        fontStyle: elemento.propiedades.cursiva ? 'italic' : 'normal',
                                        textAlign: elemento.propiedades.alineacion || 'left'
                                    });
                                    tempFabricCanvas.add(text);
                                    break;

                                case 'imagen':
                                    if (elemento.propiedades.cdnUrl || elemento.propiedades.url) {
                                        try {
                                            // Usar el proxy para cargar la imagen con CORS
                                            const imageKey = elemento.propiedades.key;
                                            const originalUrl = elemento.propiedades.cdnUrl || elemento.propiedades.url;

                                            let imageUrl = originalUrl;

                                            // Si tenemos el key, usar el proxy
                                            if (imageKey) {
                                                const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';
                                                imageUrl = `${baseUrl}/infografias/imagen/${imageKey}`;

                                                // Cargar con autenticación
                                                let token = null;
                                                try {
                                                    const storedData = localStorage.getItem('auth-storage-petitprime');
                                                    if (storedData) {
                                                        const parsedData = JSON.parse(storedData);
                                                        token = parsedData?.state?.token;
                                                    }
                                                } catch (error) {
                                                    console.warn('Error accessing localStorage:', error);
                                                }

                                                // Cargar como blob con autenticación
                                                const response = await fetch(imageUrl, {
                                                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                                                });
                                                const blob = await response.blob();
                                                imageUrl = URL.createObjectURL(blob);
                                            }

                                            const img = await FabricImage.fromURL(imageUrl, {
                                                crossOrigin: 'anonymous'
                                            });
                                            img.set({
                                                left: elemento.posicion.x,
                                                top: elemento.posicion.y,
                                                scaleX: elemento.dimensiones.ancho / img.width,
                                                scaleY: elemento.dimensiones.alto / img.height,
                                                opacity: elemento.propiedades.opacidad || 1
                                            });
                                            tempFabricCanvas.add(img);

                                            // Limpiar blob URL si se creó
                                            if (imageKey && imageUrl.startsWith('blob:')) {
                                                URL.revokeObjectURL(imageUrl);
                                            }
                                        } catch (error) {
                                            console.error('Error cargando imagen:', error);
                                        }
                                    }
                                    break;

                                case 'qr':
                                    try {
                                        const qrDataUrl = await QRCode.toDataURL(elemento.propiedades.url || 'https://example.com', {
                                            width: elemento.propiedades.tamaño || 200,
                                            margin: 2,
                                            color: {
                                                dark: elemento.propiedades.color || '#000000',
                                                light: elemento.propiedades.colorFondo || '#FFFFFF'
                                            }
                                        });
                                        const qrImg = await FabricImage.fromURL(qrDataUrl);
                                        qrImg.set({
                                            left: elemento.posicion.x,
                                            top: elemento.posicion.y,
                                            scaleX: elemento.dimensiones.ancho / qrImg.width,
                                            scaleY: elemento.dimensiones.alto / qrImg.height
                                        });
                                        tempFabricCanvas.add(qrImg);
                                    } catch (error) {
                                        console.error('Error generando QR:', error);
                                    }
                                    break;

                                case 'forma':
                                    if (elemento.propiedades.tipo === 'rectangulo') {
                                        const rect = new Rect({
                                            left: elemento.posicion.x,
                                            top: elemento.posicion.y,
                                            width: elemento.dimensiones.ancho,
                                            height: elemento.dimensiones.alto,
                                            fill: elemento.propiedades.colorRelleno || '#3B82F6',
                                            stroke: elemento.propiedades.colorBorde || '#000000',
                                            strokeWidth: elemento.propiedades.grosorBorde || 2,
                                            opacity: elemento.propiedades.opacidad || 1
                                        });
                                        tempFabricCanvas.add(rect);
                                    } else if (elemento.propiedades.tipo === 'circulo') {
                                        const circle = new Circle({
                                            left: elemento.posicion.x,
                                            top: elemento.posicion.y,
                                            radius: elemento.dimensiones.ancho / 2,
                                            fill: elemento.propiedades.colorRelleno || '#3B82F6',
                                            stroke: elemento.propiedades.colorBorde || '#000000',
                                            strokeWidth: elemento.propiedades.grosorBorde || 2,
                                            opacity: elemento.propiedades.opacidad || 1
                                        });
                                        tempFabricCanvas.add(circle);
                                    }
                                    break;

                                case 'icono':
                                    // Para iconos, mostrar un placeholder simple
                                    const iconRect = new Rect({
                                        left: elemento.posicion.x,
                                        top: elemento.posicion.y,
                                        width: elemento.dimensiones.ancho,
                                        height: elemento.dimensiones.alto,
                                        fill: elemento.propiedades.color || '#000000',
                                        opacity: elemento.propiedades.opacidad || 1
                                    });
                                    tempFabricCanvas.add(iconRect);
                                    break;
                            }
                        } catch (error) {
                            console.error('Error cargando elemento:', error);
                        }
                    }

                    // Renderizar y obtener imagen
                    tempFabricCanvas.renderAll();

                    // Esperar un momento para que todo se renderice
                    await new Promise(resolve => setTimeout(resolve, 500));

                    const dataURL = tempFabricCanvas.toDataURL({
                        format: 'png',
                        quality: 1,
                        multiplier: 1
                    });

                    setPreviewImage(dataURL);
                    setIsGeneratingPreview(false);

                    // Limpiar
                    tempFabricCanvas.dispose();
                } catch (error) {
                    console.error('Error generando preview:', error);
                    setIsGeneratingPreview(false);
                }
            };

            generatePreview();
            return;
        }

        if (fabricCanvas) {
            setIsGeneratingPreview(true);
            // Esperar un momento para asegurar que el canvas esté completamente renderizado
            const timer = setTimeout(async () => {
                try {
                    // Verificar que el canvas tenga elementos
                    if (!fabricCanvas || !fabricCanvas.getContext) {
                        throw new Error('Canvas no válido');
                    }

                    // Preparar imágenes para exportación (recargar con proxy CORS)
                    if (prepareForExport) {
                        try {
                            await prepareForExport();
                            // Esperar un momento para que las imágenes se carguen
                            await new Promise(resolve => setTimeout(resolve, 500));
                        } catch (error) {
                            console.warn('Error preparando imágenes para exportación:', error);
                            // Continuar aunque falle la preparación
                        }
                    }

                    // Forzar renderizado antes de exportar
                    fabricCanvas.renderAll();

                    // Intentar con diferentes métodos según la versión de Fabric.js
                    let dataURL;

                    // Método 1: toDataURL con opciones (Fabric.js v6)
                    try {
                        dataURL = fabricCanvas.toDataURL({
                            format: 'png',
                            quality: 1,
                            multiplier: 1
                        });
                    } catch (e) {
                        // Método 2: toDataURL simple
                        try {
                            dataURL = fabricCanvas.toDataURL('image/png');
                        } catch (e2) {
                            // Método 3: Usar el elemento canvas directamente
                            const canvasElement = fabricCanvas.getElement();
                            if (canvasElement) {
                                try {
                                    dataURL = canvasElement.toDataURL('image/png');
                                } catch (e3) {
                                    // Si falla por CORS, mostrar mensaje informativo
                                    throw new Error('No se pudo exportar el canvas debido a restricciones CORS en las imágenes. El backend debe configurar CORS en Digital Ocean Spaces para permitir la exportación.');
                                }
                            } else {
                                throw new Error('No se pudo obtener el elemento canvas');
                            }
                        }
                    }

                    if (dataURL) {
                        setPreviewImage(dataURL);
                        setIsGeneratingPreview(false);
                    } else {
                        throw new Error('No se pudo generar la imagen');
                    }
                } catch (error) {
                    console.error('Error generando preview:', error);
                    setIsGeneratingPreview(false);
                    Swal.fire({
                        title: 'Error',
                        text: `No se pudo generar el preview: ${error.message || 'Error desconocido'}`,
                        icon: 'error'
                    });
                }
            }, 200);

            return () => clearTimeout(timer);
        } else {
            setIsGeneratingPreview(false);
        }
    }, [fabricCanvas, canvasData, prepareForExport]);

    const handleExportPNG = async () => {
        if (!fabricCanvas || isExportingPNG) {
            return;
        }

        setIsExportingPNG(true);

        try {
            // Preparar imágenes para exportación (recargar con proxy CORS)
            if (prepareForExport) {
                try {
                    await prepareForExport();
                    // Esperar un momento para que las imágenes se carguen
                    await new Promise(resolve => setTimeout(resolve, 500));
                } catch (error) {
                    console.warn('Error preparando imágenes para exportación:', error);
                    // Continuar aunque falle la preparación
                }
            }

            // Forzar renderizado antes de exportar
            fabricCanvas.renderAll();

            // Esperar un momento para asegurar que el renderizado esté completo
            await new Promise(resolve => setTimeout(resolve, 100));

            // Usar toDataURL del canvas de Fabric.js directamente
            let dataURL;
            try {
                dataURL = fabricCanvas.toDataURL({
                    format: 'png',
                    quality: 1,
                    multiplier: 2 // Para mejor calidad (2x)
                });
            } catch (e) {
                // Intentar método alternativo
                const canvasElement = fabricCanvas.getElement();
                if (canvasElement) {
                    dataURL = canvasElement.toDataURL('image/png');
                } else {
                    throw new Error('No se pudo obtener el elemento canvas');
                }
            }

            const link = document.createElement('a');
            link.download = `infografia-${marca?.nombre || 'infografia'}-${Date.now()}.png`;
            link.href = dataURL;
            link.click();

            Swal.fire({
                title: '¡Exportado!',
                text: 'La infografía se ha descargado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error exportando PNG:', error);
            Swal.fire({
                title: 'Error',
                text: `No se pudo exportar la infografía: ${error.message || 'Error desconocido'}`,
                icon: 'error'
            });
        } finally {
            setIsExportingPNG(false);
        }
    };

    const handleExportPDF = async () => {
        if (!fabricCanvas || isExportingPDF) {
            return;
        }

        setIsExportingPDF(true);

        try {
            // Preparar imágenes para exportación (recargar con proxy CORS)
            if (prepareForExport) {
                try {
                    await prepareForExport();
                    // Esperar un momento para que las imágenes se carguen
                    await new Promise(resolve => setTimeout(resolve, 500));
                } catch (error) {
                    console.warn('Error preparando imágenes para exportación:', error);
                    // Continuar aunque falle la preparación
                }
            }

            // Forzar renderizado antes de exportar
            fabricCanvas.renderAll();

            // Esperar un momento para asegurar que el renderizado esté completo
            await new Promise(resolve => setTimeout(resolve, 100));

            // Obtener imagen del canvas
            let imgData;
            try {
                imgData = fabricCanvas.toDataURL({
                    format: 'png',
                    quality: 1,
                    multiplier: 2
                });
            } catch (e) {
                // Intentar método alternativo
                const canvasElement = fabricCanvas.getElement();
                if (canvasElement) {
                    imgData = canvasElement.toDataURL('image/png');
                } else {
                    throw new Error('No se pudo obtener el elemento canvas');
                }
            }

            // Verificar que tenemos la imagen
            if (!imgData || imgData === 'data:,') {
                throw new Error('No se pudo generar la imagen del canvas');
            }

            // Calcular dimensiones en mm para el PDF
            // Usar 96 DPI (DPI estándar de pantalla) en lugar de 300 para evitar PDFs demasiado grandes
            const dpi = 96;
            const widthInMm = (canvasData.dimensiones.ancho / dpi) * 25.4;
            const heightInMm = (canvasData.dimensiones.alto / dpi) * 25.4;

            // Limitar el tamaño máximo del PDF (A4 es aproximadamente 210x297mm)
            // Si es más grande, escalar proporcionalmente
            const maxWidth = 210; // Ancho máximo A4
            const maxHeight = 297; // Alto máximo A4
            let finalWidth = widthInMm;
            let finalHeight = heightInMm;

            if (widthInMm > maxWidth || heightInMm > maxHeight) {
                const scaleX = maxWidth / widthInMm;
                const scaleY = maxHeight / heightInMm;
                const scale = Math.min(scaleX, scaleY);
                finalWidth = widthInMm * scale;
                finalHeight = heightInMm * scale;
            }

            // Crear PDF con formato personalizado o A4 si es muy grande
            const isLandscape = canvasData.dimensiones.ancho > canvasData.dimensiones.alto;

            // Si las dimensiones son razonables, usar formato personalizado
            // Si son muy grandes, usar A4 y escalar
            let pdf;
            if (finalWidth <= maxWidth && finalHeight <= maxHeight) {
                pdf = new jsPDF({
                    orientation: isLandscape ? 'landscape' : 'portrait',
                    unit: 'mm',
                    format: [finalWidth, finalHeight]
                });
            } else {
                // Usar A4 y escalar la imagen
                pdf = new jsPDF({
                    orientation: isLandscape ? 'landscape' : 'portrait',
                    unit: 'mm',
                    format: 'a4'
                });
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const scaleX = pageWidth / widthInMm;
                const scaleY = pageHeight / heightInMm;
                const scale = Math.min(scaleX, scaleY);
                finalWidth = widthInMm * scale;
                finalHeight = heightInMm * scale;
            }

            // Añadir la imagen al PDF
            // Usar un pequeño margen para evitar problemas de renderizado
            const margin = 1;
            pdf.addImage(
                imgData,
                'PNG',
                margin,
                margin,
                finalWidth - (margin * 2),
                finalHeight - (margin * 2),
                undefined,
                'FAST' // Modo rápido para mejor compatibilidad
            );

            pdf.save(`infografia-${marca?.nombre || 'infografia'}-${Date.now()}.pdf`);

            Swal.fire({
                title: '¡Exportado!',
                text: 'La infografía se ha descargado correctamente',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Error exportando PDF:', error);
            Swal.fire({
                title: 'Error',
                text: `No se pudo exportar la infografía: ${error.message || 'Error desconocido'}`,
                icon: 'error'
            });
        } finally {
            setIsExportingPDF(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
                {/* Overlay */}
                <div
                    className="fixed inset-0 bg-gray-500 opacity-75 transition-opacity"
                    onClick={onClose}
                />

                {/* Modal */}
                <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">
                            Preview y Exportación
                        </h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <FaTimes className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Preview */}
                    <div className="p-6 overflow-auto max-h-[70vh] flex justify-center items-center">
                        {isGeneratingPreview ? (
                            <div className="flex flex-col items-center justify-center gap-4">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                                <p className="text-gray-600">Generando preview...</p>
                            </div>
                        ) : previewImage ? (
                            <img
                                ref={previewRef}
                                src={previewImage}
                                alt="Preview de la infografía"
                                className="max-w-full h-auto border border-gray-300 shadow-lg"
                                style={{
                                    maxHeight: '70vh'
                                }}
                            />
                        ) : (
                            <div
                                className="bg-white mx-auto flex items-center justify-center text-gray-400"
                                style={{
                                    width: `${Math.min(canvasData.dimensiones.ancho, 800)}px`,
                                    height: `${Math.min(canvasData.dimensiones.alto, 600)}px`,
                                    backgroundColor: canvasData.fondo.tipo === 'color'
                                        ? (typeof canvasData.fondo.valor === 'string'
                                            ? canvasData.fondo.valor
                                            : canvasData.fondo.valor?.color || '#FFFFFF')
                                        : '#FFFFFF'
                                }}
                            >
                                No se pudo generar el preview
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            {isPreviewOnly ? 'Cerrar' : 'Cancelar'}
                        </button>
                        {!isPreviewOnly && (
                            <>
                                <button
                                    onClick={handleExportPNG}
                                    disabled={isExportingPNG || isExportingPDF || !fabricCanvas}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isExportingPNG ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Preparando...
                                        </>
                                    ) : (
                                        <>
                                            <FaDownload />
                                            Exportar PNG
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleExportPDF}
                                    disabled={isExportingPNG || isExportingPDF || !fabricCanvas}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isExportingPDF ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Preparando...
                                        </>
                                    ) : (
                                        <>
                                            <FaDownload />
                                            Exportar PDF
                                        </>
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviewModal;

